import { useState } from "react";
import {
  Icon,
  TextField,
  Box,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Clear } from "@mui/icons-material";

import { ReactComponent as IconDiamond } from "../../images/icon-diamond.svg";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import InputMask from "react-input-mask";

import PropTypes from "prop-types";

const styles = {
  selectDadosGerais: {},
  titleDadosPessoais: {
    fontFamily: "Nunito",
    fontWeight: "600",
    fontSize: "16px",
    lineHeight: "22px",
    color: "#063F5E",
    marginBottom: "4px",
  },
  fieldset: {
    borderRadius: "5px",
    color: "#333333 !important",
    border: "1px solid #1E729F !important",
  },
  helperTextError: {
    fontFamily: "Noto Sans",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#8A0202",
    margin: "0px",
    marginTop: "5px",
    backgroundColor: "transparent",
    paddingLeft: "0px",
  },
};

export const IconMarkRequired = () => (
  <Icon sx={{ mt: "-7px" }}>
    <IconDiamond fill="#FC8518" />
  </Icon>
);

export const Label = ({ title, markeRequired = false }) => (
  <Stack component="div" direction={"row"} alignItems="center">
    <Box component="label" style={styles.titleDadosPessoais}>
      {title}
    </Box>
    {markeRequired && <IconMarkRequired />}
  </Stack>
);

/**
 * Componente FieldInputText.
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.value - O valor do TextField.
 * @param {Function} props.setValue - A função para definir o valor do TextField.
 * @param {string} props.label - O rótulo do TextField.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {string} props.unit - unit O rótulo do TextField referente ao unidade.
 * @param {TextFieldProps} props.props - Outras propriedades do componente TextField.
 */
export const FieldInputText = ({
  value,
  setValue,
  label,
  isEditField = true,
  unit = "",
  ...props
}) => {
  const error = props?.error;
  return (
    <Box style={styles.selectDadosGerais}>
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <TextField
        value={value}
        fullWidth
        onChange={(event) => {
          setValue(event.target.value);
        }}
        endAdornment={<spa>m/s</spa>}
        suffix="m/s"
        InputProps={{
          sx: isEditField
            ? {
                "& fieldset": {
                  ...(!error && !props.disabled && styles.fieldset),
                },
              }
            : {
                "& fieldset": {
                  border: "none",
                },
                "& input": {
                  padding: "6px 0px !important",
                  color: "#737373 !important",
                },
              },
          ...(unit && {
            endAdornment: (
              <InputAdornment position="end">{unit}</InputAdornment>
            ),
          }),
        }}
        sx={{
          "&  .MuiFormHelperText-root.Mui-error": styles.helperTextError,
        }}
        disabled={props.disabled ? true : !isEditField}
        {...props}
      />
    </Box>
  );
};

export const FieldTextarea = ({
  label,
  isEditField = true,
  value,
  setValue,
  ...props
}) => {
  const isMode = props.disabled ? true : !isEditField;
  const borderColor = isMode ? "#c4c4c4" : "#1E729F";
  return (
    <Box style={styles.selectDadosGerais}>
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="fieldTexteare"
        placeholder="Descreva"
        style={{
          height: "5rem",
          border: `1px solid ${borderColor}`,
          borderRadius: "5px",
          padding: "8px 16px",
          resize: "vertical",
          width: "100%",
          outline: "0",
        }}
        disabled={props.disabled ? true : !isEditField}
        {...props}
      />
    </Box>
  );
};

/**
 * Componente FieldDatePicker.
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.value - O valor sera formadato dayjs(value) do DatePicker.
 * @param {Function} props.setValue - A função para definir o valor do DatePicker.
 * @param {string} props.label - O rótulo do DatePicker.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {DatePickerProps} props.props - Outras propriedades do componente DatePicker.
 */
export const FieldDatePicker = ({
  value,
  setValue,
  label,
  isEditField = true,
  labelDatePicker = "",
  ...props
}) => {
  const isValue = value !== undefined && value !== null && value !== "";
  const [error, setNewError] = useState(props.error);

  props.helperText =
    props.error || error ? "Informe uma data válida" : props.helperText;

  const handleSetError = (error) => {
    if (error === null || error === undefined) {
      setNewError(false);
    } else {
      setNewError(true);
    }
  };

  return (
    <Box width="100%">
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <Box position="relative" width="100%">
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={"pt-br"}
        >
          <DatePicker
            label=""
            value={isValue ? dayjs(value) : null}
            onChange={(newValeu) => {
              setValue(newValeu);
            }}
            onError={(newError) => handleSetError(newError)}
            renderInput={(params) => (
              <TextField disabled fullWidth variant="standard" {...params} />
            )}
            sx={
              isEditField
                ? {
                    width: "100%",
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        ...(!error && !props.disabled && styles.fieldset),
                      },
                    "& label[data-shrink='false']": {
                      marginTop: "-7px !important",
                    },
                    "&  .MuiFormHelperText-root.Mui-error":
                      styles.helperTextError,
                  }
                : {
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        border: "none",
                      },
                    "& input": {
                      padding: "6px 0px !important",
                      color: "#737373 !important",
                    },
                    "& svg": { display: "none" },
                  }
            }
            disabled={props.disabled ? true : !isEditField}
            slotProps={{
              textField: {
                required: props.required ? true : false,
                helperText: props.helperText,
              },
              actionBar: {
                actions: ["clear"],
              },
            }}
            {...props}
          />
          <IconButton
            sx={{
              display:
                value === "" || value === null || value === undefined
                  ? "none"
                  : "",
              right: "40px",
              position: "absolute",
            }}
            onClick={() => setValue(null)}
          >
            <Clear />
          </IconButton>
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

/**
 * Componente FieldDatePicker.
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.value - O valor sera formadato dayjs(value) do DatePicker.
 * @param {Function} props.setValue - A função para definir o valor do DatePicker.
 * @param {string} props.label - O rótulo do DatePicker.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {DatePickerProps} props.props - Outras propriedades do componente DatePicker.
 */
export const FieldTimePicker = ({
  value,
  setValue,
  label,
  isEditField = true,
  labelDatePicker = "",
  ...props
}) => {
  const isValue = value !== undefined && value !== null && value !== "";
  const [error, setNewError] = useState(props.error);

  props.helperText =
    props.error || error ? "Informe uma hora válida" : props.helperText;
  const handleSetError = (error) => {
    if (error === null || error === undefined) {
      setNewError(false);
    } else {
      setNewError(true);
    }
  };

  return (
    <Box width="100%">
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <Box position="relative" width="100%">
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={"pt-br"}
        >
          <TimePicker
            label=""
            value={isValue ? dayjs(value) : null}
            onChange={(newValue) => {
              setValue(newValue);
            }}
            onError={(newError) => handleSetError(newError)}
            sx={
              isEditField
                ? {
                    width: "100%",
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        ...(!error && !props.disabled && styles.fieldset),
                      },
                    "& label[data-shrink='false']": {
                      marginTop: "-7px !important",
                    },
                    "& .MuiFormHelperText-root.Mui-error":
                      styles.helperTextError,
                  }
                : {
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        border: "none",
                      },
                    "& input": {
                      padding: "6px 0px !important",
                      color: "#737373 !important",
                    },
                    "& svg": { display: "none" },
                  }
            }
            disabled={props.disabled ? true : !isEditField}
            slotProps={{
              textField: {
                variant: "outlined",
                required: props.required ? true : false,
                helperText: props.helperText,
              },
              actionBar: {
                actions: ["clear"], // Added clear action to the picker
              },
            }}
            {...props}
          />
          {isEditField && (
            <IconButton
              sx={{
                display:
                  value === "" || value === null || value === undefined
                    ? "none"
                    : "",
                right: "40px",
                position: "absolute",
              }}
              onClick={() => setValue(null)}
            >
              <Clear />
            </IconButton>
          )}
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

/**
 * Componente FieldSelect.
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.value - O valor  do Select.
 * @param {Function} props.setValue - A função para definir o valor do Select.
 * @param {string} props.label - O rótulo do Select.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {Array<{id:number, label:string}>} props.items - Um array de objetos do tipo {id:number, label:string}.
 * @param {SelectProps} props.props - Outras propriedades do componente Select.
 */
export const FieldSelect = ({
  label,
  value,
  setValue,
  items,
  isEditField = true,
  ...props
}) => {
  return (
    <Box style={styles.selectDadosGerais}>
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />
      <FormControl fullWidth>
        <Select
          displayEmpty
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          sx={
            isEditField
              ? {
                  "& fieldset": { ...(!props.disabled && styles.fieldset) },
                }
              : {
                  "& fieldset": { border: "none" },
                  "& svg": { display: "none" },
                  "& .MuiSelect-select": { padding: "6px 0px !important" },
                }
          }
          disabled={props.disabled ? true : !isEditField}
          {...props}
          endAdornment={
            <IconButton
              sx={{
                display:
                  value === "" || value === null || value === undefined
                    ? "none"
                    : "",
                marginRight: "10px",
              }}
              onClick={() => setValue("")}
              disabled={props.disabled}
            >
              <Clear />
            </IconButton>
          }
        >
          <MenuItem disabled value="">
            <span style={{ color: "#737373" }}>Selecione</span>
          </MenuItem>
          {items.map((i, index) => {
            return (
              <MenuItem value={i.id} key={index}>
                {i.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

/**
 * Componente FieldAutocomplete.
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.value - O valor do Autocomplete.
 * @param {Function} props.setValue - A função para definir o valor do Autocomplete.
 * @param {string} props.label - O rótulo do Autocomplete.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {Array<any>} props.options - As opções disponíveis para seleção no Autocomplete.
 * @param {Function}props.getOptionLabel - Função para obter o rótulo de exibição de uma opção selecionada.
 * @param {AutocompleteProps} props.props - Outras propriedades do componente Autocomplete.
 */
export const FieldAutocomplete = ({
  label,
  value,
  setValue,
  isEditField = true,
  options,
  getOptionLabel,
  placeholder = "Selecione",
  sx = undefined,
  ...props
}) => {
  return (
    <Box style={styles.selectDadosGerais} sx={sx}>
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <Autocomplete
        disabled={props.disabled ? true : !isEditField}
        options={options}
        getOptionLabel={getOptionLabel}
        onChange={setValue}
        value={value || null}
        sx={
          isEditField
            ? {
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  ...styles.fieldset,
                },
              }
            : {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0",
                  padding: "0 !important",
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiOutlinedInput-root svg": {
                  display: "none",
                },
                "& .MuiOutlinedInput-root input": {
                  padding: "0 !important",
                },
              }
        }
        renderInput={(params) => (
          <TextField
            required={props.required}
            variant="outlined"
            disabled={props.disabled ? true : !isEditField}
            placeholder={placeholder}
            {...params}
          />
        )}
        {...props}
      />
    </Box>
  );
};

/**
 * Componente FieldInputMask.
 * @param {Object} props - As propriedades do componente.
 * * @param {string} props.mask - Mascara do Input default=""
 * @param {string} props.value - O valor do TextField.
 * @param {Function} props.setValue - A função para definir o valor do TextField.
 * @param {string} props.label - O rótulo do TextField.
 * @param {boolean} props.isEditField - Alternar entre o modo de exibição de texto e input.
 * @param {boolean} props.required - Se verdadeiro, mostra um ícone indicando que o campo é obrigatório.
 * @param {string} props.unit - unit O rótulo do TextField referente ao unidade.
 * @param {TextFieldProps} props.props - Outras propriedades do componente TextField.
 */
export const FieldInputMask = ({
  mask = "",
  label,
  value,
  setValue,
  isEditField = true,
  unit,
  ...props
}) => {
  return (
    <Box style={styles.selectDadosGerais}>
      <Label
        title={label}
        markeRequired={props.required ? isEditField : false}
      />

      <InputMask
        mask={mask}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        disabled={props.disabled ? true : !isEditField}
        {...props}
      >
        {() => (
          <TextField
            disabled={props.disabled ? true : !isEditField}
            variant="outlined"
            placeholder={props.placeholder}
            fullWidth
            InputProps={{
              sx: isEditField
                ? {
                    "& fieldset": {
                      ...(!props.error && !props.disabled && styles.fieldset),
                    },
                  }
                : {
                    "& fieldset": {
                      border: "none",
                    },
                    "& input": {
                      padding: "6px 0px !important",
                      color: "#737373 !important",
                    },
                  },
              ...(unit && {
                endAdornment: (
                  <InputAdornment position="end">{unit}</InputAdornment>
                ),
              }),
            }}
            error={props.error}
            sx={{
              "&  .MuiFormHelperText-root.Mui-error": styles.helperTextError,
            }}
            {...props}
          />
        )}
      </InputMask>
    </Box>
  );
};
